import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import React, { useEffect, useRef } from 'react';
// todo replace these with useMultiDrop and useMultiDrag from react-dnd-multi-backend
import { useDrag, useDrop } from 'react-dnd';
import classNames from 'classnames';
import Icon from 'ziphy-web-shared/basic/assets/images/Icon';
import DragAndDropHelpers from './DragAndDrop.helpers';
import styles from './DragAndDropItem.module.scss';
var getSiblingElementsInRow = DragAndDropHelpers.getSiblingElementsInRow;
var setElementFocusLine = DragAndDropHelpers.setElementFocusLine;
var getHoverOptions = DragAndDropHelpers.getOptions;
var removeElementFocusLine = DragAndDropHelpers.removeElementFocusLine;
var setLineBeforeElement = DragAndDropHelpers.setLineBeforeElement;
var setLineAfterElement = DragAndDropHelpers.setLineAfterElement;
var removeElementsMovements = DragAndDropHelpers.removeElementsMovements;
var setElementsMovements = DragAndDropHelpers.setElementsMovements;
const DragAndDropItem = ({ index, item, dropAreaId, onRemove, onMove, hasPopup, hasClose, isUnsupported, isFocused, isDisabled, }) => {
    const ref = useRef(null);
    const dropIndex = useRef(null);
    const [{ handlerId, isOver }, drop] = useDrop({
        accept: dropAreaId,
        collect(monitor) {
            return {
                handlerId: monitor.getHandlerId(),
                isOver: monitor.isOver(),
            };
        },
        drop: (item) => {
            if (dropIndex.current !== null) {
                onMove(item.index, dropIndex.current);
                dropIndex.current = null;
            }
        },
        hover(item, monitor) {
            if (!ref.current) {
                return;
            }
            const dragElement = item.ref.current;
            const hoverElement = ref.current;
            const dragIndex = item.index;
            const hoverIndex = index;
            // Don't replace items with themselves
            if (dragIndex === hoverIndex) {
                return;
            }
            // has memoization
            const options = getHoverOptions(dragElement, hoverElement, monitor);
            if (!options.isUpdated) {
                return;
            }
            const rowElements = getSiblingElementsInRow(hoverElement, options.hoverElement.boundingRect);
            const hoverElementIndexInRow = rowElements.findIndex((item) => item.element === hoverElement);
            const removeFocus = () => {
                removeElementFocusLine(hoverElement);
                removeElementsMovements(rowElements);
                dropIndex.current = null;
            };
            const setFocus = (side) => {
                if (side === 'auto') {
                    if (options.hoverElement.middleX < options.mouse.x) {
                        dropIndex.current = dragIndex > hoverIndex ? hoverIndex + 1 : hoverIndex;
                    }
                    else {
                        dropIndex.current =
                            dragIndex > hoverIndex || hoverIndex === 0 ? hoverIndex : hoverIndex - 1;
                    }
                    setElementFocusLine(hoverElement, options);
                    setElementsMovements(rowElements, options.hoverElement.middleX < options.mouse.x
                        ? hoverElementIndexInRow + 1
                        : hoverElementIndexInRow);
                }
                else if (side === 'before') {
                    dropIndex.current =
                        dragIndex > hoverIndex || hoverIndex === 0 ? hoverIndex : hoverIndex - 1;
                    setLineBeforeElement(hoverElement);
                    setElementsMovements(rowElements, hoverElementIndexInRow);
                }
                else {
                    dropIndex.current = dragIndex > hoverIndex ? hoverIndex + 1 : hoverIndex;
                    setLineAfterElement(hoverElement);
                    setElementsMovements(rowElements, hoverElementIndexInRow + 1);
                }
            };
            // 1. Case when user move dragged element in the same row
            if (options.dragElement.boundingRect.top === options.hoverElement.boundingRect.top) {
                if (dragIndex === hoverIndex + 1 && options.hoverElement.middleX < options.mouse.x) {
                    removeFocus();
                }
                else if (dragIndex === hoverIndex - 1 &&
                    options.hoverElement.middleX > options.mouse.x) {
                    removeFocus();
                }
                else {
                    setFocus('auto');
                }
                return;
            }
            // 2. Case when user move dragged element to upper rows
            if (options.dragElement.boundingRect.top > options.hoverElement.boundingRect.top) {
                if (rowElements.length > 1) {
                    setFocus('auto');
                }
                else if (rowElements[rowElements.length - 1].element.nextElementSibling === dragElement) {
                    if ((options.hoverElement.middleY * 0.8 < options.mouse.y &&
                        options.hoverElement.middleX * 0.2 < options.mouse.x) ||
                        options.hoverElement.middleX * 1.8 < options.mouse.x) {
                        removeFocus();
                    }
                    else {
                        setFocus('before');
                    }
                }
                else {
                    if ((options.hoverElement.middleY * 0.8 < options.mouse.y &&
                        options.hoverElement.middleX * 0.2 < options.mouse.x) ||
                        options.hoverElement.middleX * 1.5 < options.mouse.x) {
                        setFocus('after');
                    }
                    else {
                        setFocus('before');
                    }
                }
                return;
            }
            // 3. Case when user move dragged element to below rows
            else {
                if (rowElements.filter((item) => item.element.classList.contains(styles.dropFocus)).length >
                    1) {
                    setFocus('auto');
                }
                else if (rowElements[0].element.previousElementSibling === dragElement) {
                    if ((options.mouse.y < options.hoverElement.middleY * 0.5 &&
                        options.mouse.x < options.hoverElement.middleX * 1.5) ||
                        options.mouse.x < options.hoverElement.middleX * 0.2) {
                        removeFocus();
                    }
                    else {
                        setFocus('after');
                    }
                }
                else {
                    if ((options.mouse.y < options.hoverElement.middleY * 0.5 &&
                        options.mouse.x < options.hoverElement.middleX * 1.5) ||
                        options.mouse.x < options.hoverElement.middleX * 0.2) {
                        setFocus('before');
                    }
                    else {
                        setFocus('after');
                    }
                }
            }
        },
    }, [onMove, index]);
    const [{ isDragging }, drag] = useDrag({
        type: dropAreaId,
        item: () => ({ index, ref }),
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    }, [index]);
    useEffect(() => {
        if (!isOver) {
            dropIndex.current = null;
            const rowElements = getSiblingElementsInRow(ref.current, ref.current.getBoundingClientRect());
            removeElementsMovements(rowElements);
            removeElementFocusLine(ref.current);
        }
    }, [isOver]);
    drag(drop(ref));
    return (_jsx("div", Object.assign({ className: styles.dropFocus, ref: ref, "data-code": item.id, "data-handler-id": handlerId }, { children: _jsxs("div", Object.assign({ className: classNames(styles.item, hasPopup && styles.itemWithPopup, isUnsupported && styles.itemUnsupported, isDragging && styles.itemDrag, isFocused && styles.itemIsFocused, isDisabled && styles.itemDisabled), onClick: (e) => e.stopPropagation() }, { children: [_jsxs("span", Object.assign({ className: styles.itemText }, { children: [item.value, item.secondaryValue ? _jsxs("span", { children: ["\u00A0\u2022\u00A0", item.secondaryValue] }) : null] })), hasClose && (_jsx("div", Object.assign({ className: styles.itemClose, onClick: onRemove }, { children: _jsx(Icon, { type: 'close' }) })))] })) })));
};
export default DragAndDropItem;
